import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import styles from './MicrositeTheme.module.css'
import { BASE_URL_API } from '../../../References/URLs'
import AuthContext from '../../../store/auth-context'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import {
    Save,
    History,
    Check,
    AlertCircle,
    RefreshCw,
    Image as ImageIcon,
    Code2
} from 'lucide-react'

const FONT_OPTIONS = [
    'DM Sans, sans-serif',
    'Montserrat, sans-serif',
    'Poppins, sans-serif',
    'Questrial, sans-serif',
    'Playfair Display, serif',
    'Inter, sans-serif',
    'Lora, serif',
    'Raleway, sans-serif'
]

const EMPTY_FORM = {
    primaryColor: '#0369a1',
    secondaryColor: '#6b7280',
    backgroundColor: '#ffffff',
    accentColor: '#0d1117',
    fontFamily: 'DM Sans, sans-serif',
    logoUrl: '',
    faviconUrl: '',
    customCss: ''
}

const ColorField = ({ label, value, onChange }) => (
    <div className={styles.colorField}>
        <label className={styles.label}>{label}</label>
        <div className={styles.colorInputRow}>
            <input
                type="color"
                className={styles.colorSwatch}
                value={value || '#000000'}
                onChange={(e) => onChange(e.target.value)}
            />
            <input
                type="text"
                className={styles.colorHexInput}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="#000000"
            />
        </div>
    </div>
)

const MicrositeTheme = () => {
    const authCtx = useContext(AuthContext)
    const asCtx   = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [form, setForm] = useState(EMPTY_FORM)
    const [original, setOriginal] = useState(EMPTY_FORM)
    const [toast, setToast] = useState(null)

    const isDirty = useMemo(
        () => JSON.stringify(form) !== JSON.stringify(original),
        [form, original]
    )

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3000)
    }

    const post = useCallback(async (resource, payload = {}) => {
        try {
            const res = await fetch(BASE_URL_API + resource, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: JSON.parse(authCtx?.loginData)?.partnersData?.vendorIdentifier,
                    ...payload
                })
            })
            if (!res.ok) return null
            const data = await res.json()
            if (data?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }
            return data
        } catch {
            return null
        }
    }, [authCtx, activeStore])

    const loadTheme = useCallback(async () => {
        setLoading(true)
        const res = await post('micrositeGetStoreConfig')
        if (res?.status === 'success') {
            const t = res.response?.theme || {}
            const next = {
                primaryColor: t.primaryColor || EMPTY_FORM.primaryColor,
                secondaryColor: t.secondaryColor || EMPTY_FORM.secondaryColor,
                backgroundColor: t.backgroundColor || EMPTY_FORM.backgroundColor,
                accentColor: t.accentColor || EMPTY_FORM.accentColor,
                fontFamily: t.fontFamily || EMPTY_FORM.fontFamily,
                logoUrl: t.logoUrl || '',
                faviconUrl: t.faviconUrl || '',
                customCss: t.customCss || ''
            }
            setForm(next)
            setOriginal(next)
        } else {
            showToast('Could not load theme settings. Please refresh.', 'error')
        }
        setLoading(false)
    }, [post])

    useEffect(() => {
        if (activeStore?.storeIdentifier) loadTheme()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier])

    const updateField = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }))
    }

    const handleSave = async () => {
        setSaving(true)
        const res = await post('micrositeUpdateStoreTheme', { ...form })
        setSaving(false)

        if (res?.status === 'success') {
            setOriginal(form)
            showToast('Theme saved successfully')
        } else {
            showToast('Could not save theme. Please try again.', 'error')
        }
    }

    if (loading) {
        return (
            <div className={styles.loadingWrap}>
                <RefreshCw size={18} className={styles.spinner} />
                <span>Loading theme settings…</span>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            {toast && (
                <div className={`${styles.toast} ${styles[`toast_${toast.type}`]}`}>
                    {toast.type === 'success' ? <Check size={14} /> : <AlertCircle size={14} />}
                    {toast.message}
                </div>
            )}

            <div className={styles.pageHeader}>
                <div>
                    <h1 className={styles.pageTitle}>Theme</h1>
                    <p className={styles.pageSubtitle}>
                        Colors, fonts and branding for your microsite — see changes live on the right.
                    </p>
                </div>
                <div className={styles.headerActions}>
                    <button type="button" className={styles.historyBtn}>
                        <History size={14} />
                        History
                    </button>
                    <button
                        type="button"
                        className={styles.saveBtn}
                        onClick={handleSave}
                        disabled={!isDirty || saving}
                    >
                        {isDirty && <span className={styles.unsavedDotSmall} />}
                        <Save size={14} />
                        {saving ? 'Saving…' : 'Save changes'}
                    </button>
                </div>
            </div>

            <div className={styles.layout}>
                {/* ── Form column ── */}
                <div className={styles.formCol}>
                    <section className={styles.fieldGroup}>
                        <h2 className={styles.groupTitle}>Colors</h2>
                        <p className={styles.groupDesc}>Used across buttons, headers, and highlights.</p>

                        <div className={styles.colorGrid}>
                            <ColorField label="Primary" value={form.primaryColor} onChange={(v) => updateField('primaryColor', v)} />
                            <ColorField label="Secondary" value={form.secondaryColor} onChange={(v) => updateField('secondaryColor', v)} />
                            <ColorField label="Background" value={form.backgroundColor} onChange={(v) => updateField('backgroundColor', v)} />
                            <ColorField label="Accent" value={form.accentColor} onChange={(v) => updateField('accentColor', v)} />
                        </div>
                    </section>

                    <section className={styles.fieldGroup}>
                        <h2 className={styles.groupTitle}>Typography</h2>
                        <p className={styles.groupDesc}>The font family used across your microsite.</p>

                        <label className={styles.label}>Font family</label>
                        <select
                            className={styles.select}
                            value={form.fontFamily}
                            onChange={(e) => updateField('fontFamily', e.target.value)}
                        >
                            {FONT_OPTIONS.map((f) => (
                                <option key={f} value={f}>{f.split(',')[0]}</option>
                            ))}
                        </select>
                    </section>

                    <section className={styles.fieldGroup}>
                        <h2 className={styles.groupTitle}>Logo & favicon</h2>
                        <p className={styles.groupDesc}>Shown in your header and browser tab.</p>

                        <label className={styles.label}><ImageIcon size={12} /> Logo URL</label>
                        <input
                            type="text"
                            className={styles.input}
                            value={form.logoUrl}
                            onChange={(e) => updateField('logoUrl', e.target.value)}
                            placeholder="https://example.com/logo.png"
                        />

                        <label className={styles.label}><ImageIcon size={12} /> Favicon URL</label>
                        <input
                            type="text"
                            className={styles.input}
                            value={form.faviconUrl}
                            onChange={(e) => updateField('faviconUrl', e.target.value)}
                            placeholder="https://example.com/favicon.ico"
                        />
                    </section>

                    <section className={styles.fieldGroup}>
                        <h2 className={styles.groupTitle}>Custom CSS</h2>
                        <p className={styles.groupDesc}>Advanced — override specific styles with raw CSS.</p>

                        <label className={styles.label}><Code2 size={12} /> CSS overrides</label>
                        <textarea
                            className={styles.codeTextarea}
                            rows={6}
                            value={form.customCss}
                            onChange={(e) => updateField('customCss', e.target.value)}
                            placeholder=".btn { border-radius: 10px; }"
                            spellCheck={false}
                        />
                    </section>
                </div>

                {/* ── Live preview column ── */}
                <div className={styles.previewCol}>
                    <div className={styles.previewStickyWrap}>
                        <div className={styles.previewLabel}>Live preview</div>
                        <div
                            className={styles.previewCard}
                            style={{
                                backgroundColor: form.backgroundColor,
                                fontFamily: form.fontFamily,
                                borderColor: form.accentColor + '33'
                            }}
                        >
                            <div className={styles.previewHeader} style={{ borderColor: form.accentColor + '22' }}>
                                {form.logoUrl ? (
                                    <img src={form.logoUrl} alt="Logo" className={styles.previewLogo} onError={(e) => { e.currentTarget.style.display = 'none' }} />
                                ) : (
                                    <span style={{ color: form.accentColor, fontWeight: 700, fontSize: 14 }}>
                                        Your Salon
                                    </span>
                                )}
                                <span
                                    className={styles.previewNavDot}
                                    style={{ background: form.secondaryColor }}
                                />
                                <span
                                    className={styles.previewNavDot}
                                    style={{ background: form.secondaryColor }}
                                />
                            </div>

                            <div className={styles.previewBody}>
                                <h3 style={{ color: form.accentColor, margin: '0 0 6px', fontFamily: form.fontFamily }}>
                                    Welcome to your salon
                                </h3>
                                <p style={{ color: form.secondaryColor, fontSize: 12.5, margin: '0 0 14px', fontFamily: form.fontFamily }}>
                                    This is how your headline and supporting text will look to visitors.
                                </p>
                                <button
                                    type="button"
                                    className={styles.previewBtnSample}
                                    style={{ background: form.primaryColor, fontFamily: form.fontFamily }}
                                >
                                    Book now
                                </button>
                            </div>

                            <div className={styles.previewCardRow}>
                                <div
                                    className={styles.previewMiniCard}
                                    style={{ borderColor: form.accentColor + '22' }}
                                >
                                    <div
                                        className={styles.previewMiniCardSwatch}
                                        style={{ background: form.primaryColor }}
                                    />
                                    <span style={{ color: form.accentColor, fontSize: 11, fontFamily: form.fontFamily }}>Hair</span>
                                </div>
                                <div
                                    className={styles.previewMiniCard}
                                    style={{ borderColor: form.accentColor + '22' }}
                                >
                                    <div
                                        className={styles.previewMiniCardSwatch}
                                        style={{ background: form.secondaryColor }}
                                    />
                                    <span style={{ color: form.accentColor, fontSize: 11, fontFamily: form.fontFamily }}>Skin</span>
                                </div>
                            </div>
                        </div>
                        <p className={styles.previewNote}>
                            This is a simplified preview. Your actual microsite may render slightly differently.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default MicrositeTheme